import React, { useState, useEffect, useMemo } from "react";
import { useParams } from "react-router-dom";

import {
  CreateLoanParamsBridgeLoan,
  DialogTitle,
  LoanStage
} from "@toorak/tc-common-fe-sdk";
import LoadingButton from "@mui/lab/LoadingButton";
import {
  Dialog,
  DialogContent,
  Grid,
  Checkbox,
  TextField,
  Theme
} from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import createStyles from "@mui/styles/createStyles";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "../../stores/rootReducer";
import {
  getLoanHistory,
  postLoan,
  updateSummaryDetails
} from "../create-loan.action";
import { getLoanType } from "../../config/config";
import { getBridgeLoanGuarantorInformationToPass } from "../CreateLoanHeader";
import { updatePollingData } from "../../stores/SettlementAndPurchase/SettlementAndPurchase.reducer";
import { postLoanSuccess } from "../create-loan.reducer";
import { moveToReviewInProgress } from "../../utils/updateStatus";
import { getCookie } from "../../utils/cookies";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    dialogTitle: {
      color: "#32325D",
      fontFamily: "Open Sans",
      fontSize: "14px",
      fontStyle: "normal",
      fontWeight: 600,
      lineHeight: "normal"
    },
    commentField: {
      color: "#32325D",
      fontFamily: "Open Sans",
      fontSize: "12px",
      fontStyle: "normal",
      fontWeight: 400,
      lineHeight: "18px" /* 150% */
    },
    requiredField: {
      color: "#F75676",
      fontFamily: "Open Sans",
      fontSize: "12px",
      fontStyle: "normal",
      fontWeight: 400,
      lineHeight: "18px"
    },
    acceptButton: {
      borderRadius: "4px",
      background: "#059F5D",
      boxShadow: "0px 2px 4px 0px rgba(50, 50, 93, 0.20)",
      display: "flex",
      padding: "8px 20px",
      justifyContent: "center",
      alignItems: "center",
      gap: "10px",
      color: "#FFF",
      fontFamily: "Open Sans",
      fontSize: "14px",
      fontStyle: "normal",
      fontWeight: 400,
      lineHeight: "normal"
    },
    rejectButton: {
      display: "flex",
      padding: "8px 20px",
      justifyContent: "center",
      alignItems: "center",
      gap: "10px",
      borderRadius: "4px",
      background: "#F75676",
      boxShadow: "0px 2px 4px 0px rgba(50, 50, 93, 0.20)",
      color: "#FFF",
      fontFamily: "Open Sans",
      fontSize: "14px",
      fontStyle: "normal",
      fontWeight: 400,
      lineHeight: "normal"
    },
    cancelButton: {
      display: "flex",
      padding: "8px 20px",
      justifyContent: "center",
      alignItems: "center",
      gap: "10px",
      borderRadius: "4px",
      border: "1px solid #E2E4F1",
      background: "#FFF",
      boxShadow: "0px 2px 4px 0px rgba(50, 50, 93, 0.20)",
      color: "#32325D",
      fontFamily: "Open Sans",
      fontSize: "14px",
      fontStyle: "normal",
      fontWeight: 400,
      lineHeight: "normal"
    }
  })
);

const EnhanceCreditReviewDialog = (props: any) => {
  const { enhanceCreditReview, setEnhanceCreditReview } = props;
  const [callAutoSave, setCallAutoSave] = useState<{
    value: boolean;
    commentList: any[];
  }>({ value: false, commentList: [] });
  const [comment, setComment] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [riskBucketApproved, setRiskBucketApproved] = useState<boolean>(true);

  const classes = useStyles();
  const dispatch = useDispatch();

  const { loanDetails } = useSelector<RootState, any>(
    (state) => state.createLoanStore
  );
  const { loanEvaluationResult } = useSelector<RootState, any>(
    (state) => state.evaluationResultsStore
  );
  const {
    bridgeLoanBorrowerInformation,
    bridgeLoanInformation,
    bridgeLoanEconomics,
    thirtyYearLoanEconomics,
    thirtyYearLoanInformation,
    bridgeLoanGuarantorInformation
  } = useSelector<RootState, any>((state) => state.createLoanStore.loanDetails);

  const { loanId, loanType, loanStage } = useParams<{
    loanId: string;
    loanStage: any;
    loanType: string;
  }>() as any;
  const bridgeLoanGuarantorInformationToPass =
    getBridgeLoanGuarantorInformationToPass(bridgeLoanGuarantorInformation);

  const handleCloseModal = () => {
    setEnhanceCreditReview(false);
    setRiskBucketApproved(true);
  };

  const convertBooleanToString = (value: boolean) => {
    if (value) {
      return "Yes";
      // eslint-disable-next-line no-else-return
    } else {
      return "No";
    }
  };

  const isRiskBucketApprovalRequired = useMemo(() => {
    const riskBucket =
      loanEvaluationResult?.loanResult?.[loanId]?.riskScores?.riskBucket;
    if (riskBucket) {
      return riskBucket === "4" || riskBucket === "5";
    }
    return false;
  }, [loanEvaluationResult, loanId]);

  const handleEnhancedCreditReviewChange = (value: string) => {
    const personId = getCookie("person_id");

    if (!comment) return;
    const changedValue = value;
    const commentList: any = [
      {
        value: changedValue,
        comment,
        updatedBy: personId,
        field: `data.loan.loanDetailId.loanSummaryId.enhancedReview`
      },
    ];
    if (isRiskBucketApprovalRequired) {
      commentList.push({
        value: true,
        comment: "Approved from Summary checklist",
        updatedBy: personId,
        field: `data.loan.loanDetailId.loanSummaryId.riskBucketApproved`
      });
    }
    dispatch(updateSummaryDetails("enhancedReview", changedValue, true));
    if (isRiskBucketApprovalRequired) {
      dispatch(
        updateSummaryDetails("riskBucketApproved", riskBucketApproved, true)
      );
    }
    setCallAutoSave({ value: true, commentList });
    setIsLoading(true);
  };

  const saveCheckListValue = (commentList: any[]) => {
    let loanEconomics: any;
    let loanInformation: any;
    let isDSCR: boolean;
    if (loanType === getLoanType[0].displayValue) {
      isDSCR = false;
      loanEconomics = { ...bridgeLoanEconomics.payload };
      loanInformation = { ...bridgeLoanInformation.payload };
    } else {
      isDSCR = true;
      loanEconomics = { ...thirtyYearLoanEconomics.payload };
      loanInformation = { ...thirtyYearLoanInformation.payload };
    }

    postLoan(
      {
        loanId,
        loanType,
        borrowerInformation: bridgeLoanBorrowerInformation,
        guarantorInfo: bridgeLoanGuarantorInformationToPass,
        ...loanInformation,
        ...loanEconomics,
        loanConfig: loanDetails.loanConfig,
        loanSummary: loanDetails.loanSummary
      },
      false,
      true,
      false,
      "",
      commentList
    )
      .then((response: CreateLoanParamsBridgeLoan) => {
        if (loanStage === LoanStage.post) {
          // dispatch({
          //   type: UPDATE_POLLING_DATA,
          //   payload: {
          //     startPolling: true,
          //     timeInMs: 0
          //   }
          // });
          dispatch(
            updatePollingData({
              startPolling: true,
              timeInMs: 0
            })
          );
        }
        // dispatch({
        //   type: POST_LOAN_SUCCESS,
        //   payload: {
        //     reservation: response
        //   }
        // });
        dispatch(
          postLoanSuccess({
            reservation: response
          })
        );
        moveToReviewInProgress(
          loanId,
          loanStage,
          isDSCR,
          loanDetails,
          loanEvaluationResult,
          dispatch,
          "Updated from Summary tab by the system as prerequisites were met.",
          {
            loanSummary: loanDetails.loanSummary
          }
        );
        dispatch(getLoanHistory(`${loanId}_${loanStage}`));
        setEnhanceCreditReview(false);
        setRiskBucketApproved(true);
        setComment("");
      })
      .catch((err: any) => console.error(err))
      .finally(() => {
        setIsLoading(false);
      });
  };

  useEffect(() => {
    if (callAutoSave.value) {
      saveCheckListValue(callAutoSave.commentList);
      setCallAutoSave({ value: false, commentList: [] });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [callAutoSave]);

  return (
    <Dialog
      open={enhanceCreditReview}
      onClose={() => handleCloseModal()}
      aria-labelledby="enhance-credit-review-title"
      fullWidth
    >
      <DialogTitle
        id="hold-dialog-title"
        onClose={() => handleCloseModal()}
        className={classes.dialogTitle}
      >
        Enhance Credit Review
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <span className={classes.commentField}>
              Comments <sup className={classes.requiredField}>*</sup>
            </span>
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              maxRows={4}
              placeholder="Type Here"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
            />
          </Grid>
        </Grid>
        {isRiskBucketApprovalRequired && (
          <Grid container spacing={2} style={{ marginTop: "12px" }}>
            <Grid item xs={12}>
              <Checkbox
                checked={riskBucketApproved}
                onChange={(e: any) => setRiskBucketApproved(e.target.checked)}
              />
              <label>Approving loan with 4/5 Risk bucket</label>
            </Grid>
          </Grid>
        )}
        <Grid container spacing={2} style={{ marginTop: "12px" }}>
          <Grid item xs={3}>
            <LoadingButton
              className={classes.acceptButton}
              fullWidth
              onClick={() => handleEnhancedCreditReviewChange("Yes")}
              loading={isLoading}
              variant="contained"
              disabled={!(comment?.length > 0 && riskBucketApproved)}
            >
              Approve
            </LoadingButton>
          </Grid>
          <Grid item xs={3}>
            <LoadingButton
              fullWidth
              className={classes.rejectButton}
              onClick={() => handleEnhancedCreditReviewChange("No")}
              loading={isLoading}
              variant="contained"
              disabled={comment?.length <= 0}
            >
              Reject
            </LoadingButton>
          </Grid>
          <Grid item xs={3}>
            <LoadingButton
              fullWidth
              className={classes.cancelButton}
              onClick={handleCloseModal}
              loading={isLoading}
              variant="contained"
              disabled={comment?.length <= 0}
            >
              Cancel
            </LoadingButton>
          </Grid>
        </Grid>
      </DialogContent>
    </Dialog>
  );
};

export default EnhanceCreditReviewDialog;
